import { DecimalPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { IntervalType } from '@api-types/enums/interval-type.enum';
import {
  PmIconComponent,
  PmTooltipDirective,
  SubtitleComponent,
} from '@libs/ui';

import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FeatureFlagSelectors } from 'core/store/feature-flags/feature-flags.selectors';
import {
  isForecastSeriesFetchingOrPending,
  selectConfidenceLevel,
  selectIntervalType,
  selectShowInterval,
} from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import {
  DEFAULT_CONFIDENCE_LEVEL,
  DEFAULT_INTERVAL_TYPE,
} from 'demand-forecasting/store/router-state/default-url-params';
import { selectTargetUnit } from 'demand-forecasting/store/router-state/router.selectors';
import { LucideInfo } from '@lucide/angular';
import { Subscription } from 'rxjs';

@Component({
  selector: 'pm-intervals-and-confidence-level',
  templateUrl: './intervals-and-confidence-level.component.html',
  imports: [
    MatCheckbox,
    ReactiveFormsModule,
    FormsModule,
    PmTooltipDirective,
    MatRadioGroup,
    MatRadioButton,
    MatSlider,
    MatSliderThumb,
    TranslateModule,
    DecimalPipe,
    SubtitleComponent,
    PmIconComponent,
  ],
})
export class IntervalsAndConfidenceLevelComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly store = inject(Store);

  protected readonly InfoIcon = LucideInfo;

  @Input() showInterval: boolean;
  @Output() showIntervalChange = new EventEmitter<boolean>();

  @Input() confidenceLevel: number;
  @Output() confidenceLevelChange = new EventEmitter<number>();

  @Input() intervalType: IntervalType | undefined;
  @Output() intervalTypeChange = new EventEmitter<IntervalType | undefined>();

  intervalOptions: { label: string; value: IntervalType }[] = [
    {
      label: this.translateService.instant(
        'RESULT.VISIBILITY_OPTIONS.INTERVAL_CONFIDENCE',
      ),
      value: IntervalType.CONFIDENCE,
    },
    {
      label: this.translateService.instant(
        'RESULT.VISIBILITY_OPTIONS.INTERVAL_PREDICTION',
      ),
      value: IntervalType.PREDICTION,
    },
  ];

  selectedInterval = this.store.selectSignal(selectIntervalType);
  intervalValue = this.store.selectSignal(selectConfidenceLevel);
  showIntervalFromUrl = this.store.selectSignal(selectShowInterval);
  selectedTargetUnit = this.store.selectSignal(selectTargetUnit);
  confidenceIntervalFeatureFlag = this.store.selectSignal(
    FeatureFlagSelectors.selectConfidenceIntervalFeatureFlag,
  );
  showIntervalOption = true;

  protected isLoading = this.store.selectSignal(
    isForecastSeriesFetchingOrPending,
  );

  defaultSelectedByFeatureFlag$ = this.store.select(
    FeatureFlagSelectors.selectConfidenceIntervalDefaultSelected,
  );
  subscriptions: Subscription[] = [];

  targetValue =
    (this.selectedTargetUnit() ?? 'value').charAt(0).toUpperCase() +
    (this.selectedTargetUnit() ?? 'value').slice(1);

  sliderOptions = {
    floor: 0.01,
    ceil: 0.99,
    step: 0.01,
    translate: (value: number): string => `${Math.round(value * 100)}%`,
  };

  ngOnInit(): void {
    this.showIntervalOption = this.confidenceIntervalFeatureFlag().enabled;
    this.showInterval = this.showIntervalFromUrl();
    this.confidenceLevel = this.intervalValue() || DEFAULT_CONFIDENCE_LEVEL;
    this.intervalType = this.selectedInterval() || DEFAULT_INTERVAL_TYPE;
    this.showIntervalChange.emit(this.showInterval);
    this.confidenceLevelChange.emit(this.confidenceLevel);
    this.intervalTypeChange.emit(this.intervalType);
  }

  onConfidenceLevelChange(value: number): void {
    this.confidenceLevel = value;
    this.confidenceLevelChange.emit(this.confidenceLevel);
  }

  onShowIntervalChange(value: boolean): void {
    this.showInterval = value;
    this.showIntervalChange.emit(this.showInterval);
  }

  onIntervalTypeChange(value: IntervalType): void {
    this.intervalType = value;
    this.intervalTypeChange.emit(this.intervalType);
  }
}
